import { useCallback, useEffect, useMemo, useState } from 'react';
import teamService, { TeamListItem } from '../../services/api/teamService';
import userService, { UserListItem } from '../../services/api/userService';

interface AdminFieldMetrics {
  totalUsers: number;
  activeTeams: number;
  unallocatedUsers: number;
  telegramLinked: number;
  allocationPercent: number;
}

interface AdminFieldData {
  users: UserListItem[];
  teams: TeamListItem[];
  metrics: AdminFieldMetrics;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

const firstFulfilled = <T,>(result: PromiseSettledResult<T>, fallback: T): T =>
  result.status === 'fulfilled' ? result.value : fallback;

export default function useAdminFieldData(): AdminFieldData {
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const [usersResult, teamsResult] = await Promise.allSettled([
          userService.searchUsers({ limit: 500 }),
          teamService.getTeams(),
        ]);
        if (cancelled) return;
        setUsers(firstFulfilled(usersResult, []));
        setTeams(firstFulfilled(teamsResult, []));
        if (usersResult.status === 'rejected' || teamsResult.status === 'rejected') {
          const failed = [
            usersResult.status === 'rejected' ? 'users' : null,
            teamsResult.status === 'rejected' ? 'teams' : null,
          ].filter(Boolean).join(' and ');
          setError(`Admin ${failed} could not be loaded.`);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || 'Admin data could not be loaded.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const metrics = useMemo<AdminFieldMetrics>(() => {
    const allocatedIds = new Set(
      teams.flatMap((team) => (team.members ?? []).map((member) => member.id))
    );
    const unallocatedUsers = users.filter((user) => !allocatedIds.has(user.id)).length;
    const telegramLinked = users.filter((user) => Boolean(user.telegramChatId)).length;
    const allocationPercent = users.length
      ? Math.round(((users.length - unallocatedUsers) / users.length) * 100)
      : 0;
    return {
      totalUsers: users.length,
      activeTeams: teams.length,
      unallocatedUsers,
      telegramLinked,
      allocationPercent,
    };
  }, [teams, users]);

  const reload = useCallback(() => setReloadToken((value) => value + 1), []);

  return { users, teams, metrics, loading, error, reload };
}
